// src/pages/Coleta/components/ComparacaoPontos.jsx
import React from 'react';
import { Box, Button, Grid, MenuItem, Paper, Stack, TextField, Typography, alpha } from '@mui/material';
import {
    dataDoExperimento,
    formatarContagem,
    formatarDataHora,
    formatarLog10,
    rotuloOrdenha,
} from '../../../utils/coleta';

function Linha({ children }) {
    return (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.6 }}>
            {children}
        </Typography>
    );
}

/**
 * "Uma ordenha, diferentes pontos": a mesma ordenha (dia + turno + origem) em cada
 * ponto de coleta, lado a lado. Ajuda a localizar onde a contagem sobe — não prova a
 * causa, e o texto diz isso.
 */
export default function ComparacaoPontos({
    ordenhas,
    selecionada,
    onSelecionar,
    nomePonto,
    ordemPonto,
    pontosDaAnalise = [],
    corDoPonto = () => null,
    unidade,
    rotuloMedida,
    onAbrirAmostra,
}) {
    const ordenha = ordenhas.find((o) => o.chave === selecionada) || ordenhas[0];

    // Número do card = posição do ponto no CADASTRO da análise (mais algum ponto já
    // desativado que ainda tenha coleta): "Entrada do tanque" é sempre 2, mesmo numa
    // ordenha — ou num período inteiro — sem a saída.
    const numeroDoPonto = new Map(
        [...new Set([
            ...pontosDaAnalise.map((p) => p.id),
            ...ordenhas.flatMap((o) => o.pontos.map((p) => p.pontoColetaId || null)),
        ])]
            .sort((a, b) => ordemPonto(a) - ordemPonto(b))
            .map((id, i) => [id, i + 1])
    );
    const variasOrigens = (ordenha?.origens || []).length > 1;
    const cards = ordenha
        ? [...ordenha.pontos].sort(
              (a, b) =>
                  ordemPonto(a.pontoColetaId) - ordemPonto(b.pontoColetaId) ||
                  new Date(a.dataHora) - new Date(b.dataHora)
          )
        : [];

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2.5 }}>
                <Box sx={{ flex: { md: '0 1 380px' } }}>
                    <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                        Uma ordenha, diferentes pontos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        As diferenças entre pontos ajudam a orientar uma investigação; não comprovam a causa.
                    </Typography>
                </Box>
                {ordenhas.length > 0 && (
                    <TextField
                        select
                        size="small"
                        value={ordenha?.chave || ''}
                        onChange={(e) => onSelecionar(e.target.value)}
                        sx={{ flex: 1 }}
                        inputProps={{ 'aria-label': 'Ordenha' }}
                    >
                        {ordenhas.map((o) => (
                            <MenuItem key={o.chave} value={o.chave}>
                                {rotuloOrdenha(o)}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {o.pontos.length === 1 ? '1 ponto' : `${o.pontos.length} pontos`}
                                </Typography>
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Stack>

            {cards.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Nenhuma ordenha com contagem no período.
                </Typography>
            ) : (
                <Grid container spacing={2}>
                    {cards.map((p) => {
                        const inicio = dataDoExperimento(p.inicioEnsaio);
                        const resultado = dataDoExperimento(p.fimEnsaio);
                        return (
                            <Grid item xs={12} md={4} key={p.amostraId}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2, borderRadius: 2.5, height: '100%', display: 'flex', flexDirection: 'column',
                                        // Cada ponto como um cartão próprio, destacado do painel (Figma).
                                        bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.08 : 0.045),
                                        borderColor: (t) => alpha(t.palette.primary.main, 0.2),
                                        boxShadow: (t) => `0 1px 3px ${alpha(t.palette.primary.main, 0.08)}`,
                                    }}
                                >
                                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                                        <Box
                                            sx={{
                                                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                // Mesma cor da linha do ponto no grafico: o numero preenchido
                                                // liga o card a linha. O ponto em azul-escuro (quase preto)
                                                // fica no estilo neutro, como era.
                                                ...(corDoPonto(p.pontoColetaId)
                                                    ? { bgcolor: corDoPonto(p.pontoColetaId), color: '#fff' }
                                                    : { bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main' }),
                                                fontSize: '0.75rem', fontWeight: 700,
                                            }}
                                        >
                                            {numeroDoPonto.get(p.pontoColetaId || null)}
                                        </Box>
                                        <Typography fontWeight={700} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{nomePonto(p.pontoColetaId) || 'Sem ponto'}</Typography>
                                    </Stack>

                                    <Linha>Coleta: {formatarDataHora(new Date(p.dataHora))}</Linha>
                                    {variasOrigens && p.origem && <Linha>Origem: {p.origem}</Linha>}
                                    {(p.dispositivoNome || inicio) && (
                                        <Linha>
                                            {[p.dispositivoNome, inicio && `Início: ${formatarDataHora(inicio)}`].filter(Boolean).join(' · ')}
                                        </Linha>
                                    )}
                                    {resultado && <Linha>Resultado: {formatarDataHora(resultado)}</Linha>}

                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 2.5, mb: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary">{rotuloMedida}</Typography>
                                        <Box sx={{ textAlign: 'right' }}>
                                            {/* Valor e unidade na mesma linha; o log10 logo abaixo. */}
                                            <Stack direction="row" spacing={0.75} alignItems="baseline" justifyContent="flex-end">
                                                <Typography fontWeight={800} sx={{ fontSize: '1.15rem', lineHeight: 1.2 }}>
                                                    {formatarContagem(p.log10Contagem)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">{unidade}</Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {formatarLog10(p.log10Contagem)}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Box sx={{ mt: 'auto' }}>
                                        <Button
                                            size="small"
                                            onClick={() => onAbrirAmostra(p.amostraId)}
                                            sx={{ px: 0, fontWeight: 700, textTransform: 'none' }}
                                        >
                                            Ver amostra {p.identificacao}
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Paper>
    );
}
